import React, { useState, useMemo } from "react";
import { exportToExcel } from "./utils";
import { useTranslation } from "react-i18next";
import { Search as SearchIcon, FileText } from "lucide-react";

const ITEMS_PER_PAGE = 20;
const typeColors = {
  moving: "bg-primary/10 text-primary",
  delivery: "bg-success/10 text-success",
  default: "bg-base-200 text-base-content/70",
};

const AppointmentList = ({ events, onEdit, onDelete }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  const filteredEvents = useMemo(() => {
    if (!search) return events;
    const lower = search.toLowerCase();
    return events.filter((e) => {
      const props = e.extendedProps || {};
      return [
        e.start,
        props.client,
        props.address,
        props.phone,
        props.note,
        props.type,
      ]
        .join(" ")
        .toLowerCase()
        .includes(lower);
    });
  }, [events, search]);

  const totalPages = Math.ceil(filteredEvents.length / ITEMS_PER_PAGE);
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const currentEvents = filteredEvents.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  React.useEffect(() => { setPage(1); }, [search]);

  const handleExport = () => {
    const formatted = filteredEvents.map((e) => ({
      [t("calendar.date")]: e.start?.split("T")[0],
      [t("calendar.time")]: new Date(e.start).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      [t("calendar.client")]: e.extendedProps.client,
      [t("calendar.address")]: e.extendedProps.address,
      [t("calendar.phone")]: e.extendedProps.phone,
      [t("calendar.comment")]: e.extendedProps.note || "",
      [t("calendar.type")]: e.extendedProps.type,
    }));
    exportToExcel(formatted, "Appointments");
  };

  return (
    <div className="mt-4">
      {/* Barre d'actions */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <h2 className="text-sm font-semibold text-base-content/70 flex items-center gap-2">
          📋 {t("calendar.listTitle")}
        </h2>
        <div className="flex items-center gap-3 w-full md:w-auto">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3 top-2.5 text-base-content/40" size={15} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t("calendar.searchPlaceholder") || "Recherche..."}
              className="input input-bordered input-sm pl-9 w-full bg-base-200 border-base-300 text-base-content focus:border-primary focus:outline-none"
            />
          </div>
          <button
            onClick={handleExport}
            className="btn btn-sm btn-ghost text-base-content/70 flex items-center gap-2 border border-base-300"
          >
            <FileText size={14} /> {t("calendar.exportExcel")}
          </button>
        </div>
      </div>

      {/* Tableau */}
      <div className="overflow-x-auto bg-base-100 rounded-xl border border-base-200 shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 bg-base-200 z-10">
            <tr>
              <th className="py-3 px-3 text-left text-xs font-semibold text-base-content/70">{t("calendar.date")}</th>
              <th className="py-3 px-3 text-left text-xs font-semibold text-base-content/70">{t("calendar.time")}</th>
              <th className="py-3 px-3 text-left text-xs font-semibold text-base-content/70">{t("calendar.client")}</th>
              <th className="py-3 px-3 text-left text-xs font-semibold text-base-content/70">{t("calendar.address")}</th>
              <th className="py-3 px-3 text-left text-xs font-semibold text-base-content/70">{t("calendar.phone")}</th>
              <th className="py-3 px-3 text-left text-xs font-semibold text-base-content/70">{t("calendar.comment")}</th>
              <th className="py-3 px-3 text-left text-xs font-semibold text-base-content/70">{t("calendar.type")}</th>
              <th className="py-3 px-3 text-center text-xs font-semibold text-base-content/70">{t("calendar.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {currentEvents.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-base-content/40 py-10 text-sm">
                  🗓️ {t("calendar.noAppointments")}
                </td>
              </tr>
            )}
            {currentEvents.map((e, i) => {
              const props = e.extendedProps || {};
              const type = props.type?.toLowerCase?.() || "default";
              return (
                <tr
                  key={e.id}
                  className={`border-t border-base-200 hover:bg-base-200/50 transition-colors ${
                    i % 2 === 0 ? "bg-base-100" : "bg-base-200/30"
                  }`}
                >
                  <td className="px-3 py-3 font-semibold text-base-content">{e.start?.split("T")[0]}</td>
                  <td className="px-3 py-3 text-base-content/70">
                    {new Date(e.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-3 py-3 text-base-content">{props.client}</td>
                  <td className="px-3 py-3 text-base-content/70">{props.address}</td>
                  <td className="px-3 py-3 text-base-content/70">{props.phone}</td>
                  <td className="px-3 py-3 text-base-content/50">
                    {props.note || <span className="opacity-50 italic">-</span>}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`px-2.5 py-1 rounded-full font-semibold text-xs ${typeColors[type] || typeColors.default}`}>
                      {t(`calendar.${type}`)}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => onEdit(e)}
                        title={t("calendar.edit")}
                        className="btn btn-ghost btn-xs text-warning hover:bg-warning/10"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => onDelete(e)}
                        title={t("calendar.delete")}
                        className="btn btn-ghost btn-xs text-error hover:bg-error/10"
                      >
                        🗑
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2 mt-6">
          <button
            onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            disabled={page === 1}
            className="btn btn-ghost btn-sm text-base-content/70"
          >
            « {t("calendar.prev")}
          </button>
          {[...Array(totalPages)].map((_, idx) => (
            <button
              key={idx}
              onClick={() => setPage(idx + 1)}
              className={`btn btn-sm ${
                page === idx + 1 ? "btn-primary" : "btn-ghost text-base-content/70"
              }`}
            >
              {idx + 1}
            </button>
          ))}
          <button
            onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={page === totalPages}
            className="btn btn-ghost btn-sm text-base-content/70"
          >
            {t("calendar.next")} »
          </button>
        </div>
      )}
    </div>
  );
};

export default AppointmentList;
